import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInstitution } from '../../../context/InstitutionContext';
import './ProvinceDetail.css';

interface Institution {
  id: string;
  name: string;
  shortName: string;
  city: string;
  score: number;
  rating: string;
}

interface ProvinceDetailProps {
  province: string;
  data: {
    avgScore: number;
    letterGrade: string;
    institutionCount: number;
    institutions: Institution[];
  };
  onClose: () => void;
}

// Rating color thresholds (based on BigQuery data)
// AAA: 89+, AA: 78-88, A: 68-77, BBB: 57-67, BB: 48-56, B: <48
const getRatingColor = (score: number): string => {
  if (score >= 89) return '#0d4715'; // AAA
  if (score >= 78) return '#1b5e20'; // AA
  if (score >= 68) return '#4caf50'; // A
  if (score >= 57) return '#ffc107'; // BBB
  if (score >= 48) return '#ff9800'; // BB
  return '#f44336'; // B
};

const ProvinceDetail: React.FC<ProvinceDetailProps> = ({
  province,
  data,
  onClose,
}) => {
  const { t } = useTranslation();
  const { setSelectedInstitution } = useInstitution();

  const handleInstitutionClick = (inst: Institution) => {
    setSelectedInstitution({
      id: inst.id,
      name: inst.name,
    });
  };

  // Group institutions by city, sorted by score within each city
  const institutionsByCity = data.institutions.reduce(
    (acc, inst) => {
      if (!acc[inst.city]) {
        acc[inst.city] = [];
      }
      acc[inst.city].push(inst);
      return acc;
    },
    {} as Record<string, Institution[]>
  );

  // Sort institutions within each city by score descending
  Object.values(institutionsByCity).forEach((institutions) => {
    institutions.sort((a, b) => b.score - a.score);
  });

  // Sort cities by their highest score
  const sortedCities = Object.entries(institutionsByCity).sort(
    ([, a], [, b]) => b[0].score - a[0].score
  );

  return (
    <div className="province-detail">
      <div className="province-detail-header">
        <div className="header-content">
          <h2>{province}</h2>
          <div className="province-stats">
            <span
              className="avg-rating"
              style={{ backgroundColor: getRatingColor(data.avgScore) }}
            >
              {data.avgScore.toFixed(0)}
            </span>
            <span className="avg-score">{t('map.avgScore')}</span>
          </div>
        </div>
        <button className="close-button" onClick={onClose} aria-label="Close">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="province-detail-body">
        <p className="institution-count">
          {t('map.ratedInstitutionsCount', { count: data.institutionCount })}
        </p>

        {sortedCities.map(([city, institutions]) => (
          <div key={city} className="city-section">
            <h3 className="city-name">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {city}
            </h3>
            <ul className="city-institutions">
              {institutions.map((inst) => (
                <li
                  key={inst.id}
                  className="province-institution-item"
                  onClick={() => handleInstitutionClick(inst)}
                >
                  <div className="inst-info">
                    <span className="inst-name">{inst.shortName}</span>
                  </div>
                  <span className="inst-score">{inst.score.toFixed(1)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProvinceDetail;
